import type { MusicTrack, SetDefinition } from '../../data/types';
import { clamp01 } from '../../shared/scalar';
import { pathWalked } from '../../sim/full/festival';
import type { SimState } from '../../sim/state';
import { tickToTime } from '../clock';
import { createMusic, type Music, type MusicOptions } from '../music';
import { impact } from '../music-hits';
import { createReverb, type Reverb } from '../reverb';

// Shares of the path walked, not seconds: a team that stops hears the walk stop.
const LEFT_SILENT = 0.4;
const AHEAD_FROM = 0.35;
const SWELL = 0.25;
const FAR_GAIN = 0.2;
const FAR_HZ = 150;
const FAR_WET = 0.35;
const LEFT_CLOSED_HZ = 260;
const LEFT_CLOSING = 0.85;
const LEFT_WET = 0.7 * FAR_WET;
const OPEN_HZ = 20_000;
const SILENT = 0.01;
const GLIDE_SECONDS = 0.08;
const IMPACT_GAIN = 0.5;
const TAIL_SECONDS = 4;
const FAR_ROOM = { reverbSeconds: 3.2, wet: 1, lowCutHz: 180, toneHz: 3800 };

export interface Mix {
  gain: number;
  cutoff: number;
  wet: number;
}

const OPEN: Mix = { gain: 1, cutoff: OPEN_HZ, wet: 0 };

export function leftMix(walked: number): Mix {
  const gone = Math.min(1, walked / LEFT_SILENT);
  return {
    gain: gone >= 1 ? 0 : SILENT ** gone,
    cutoff: OPEN_HZ * (LEFT_CLOSED_HZ / OPEN_HZ) ** Math.min(1, gone / LEFT_CLOSING),
    wet: LEFT_WET * gone,
  };
}

export function aheadMix(walked: number): Mix {
  const near = clamp01((walked - AHEAD_FROM) / (1 - AHEAD_FROM));
  const wet = FAR_WET * (1 - near);
  const swell = SWELL / (1 - AHEAD_FROM);
  if (near < swell) {
    return { gain: (FAR_GAIN * near) / swell, cutoff: FAR_HZ, wet };
  }
  const opening = (near - swell) / (1 - swell);
  return {
    gain: FAR_GAIN * (1 / FAR_GAIN) ** opening,
    cutoff: FAR_HZ * (OPEN_HZ / FAR_HZ) ** opening,
    wet,
  };
}

interface Voice {
  music: Music;
  input: BiquadFilterNode;
  gain: GainNode;
  send: GainNode;
}

export function createWalkingMusic(
  out: AudioNode,
  options: MusicOptions,
  trackFor?: (set: SetDefinition) => MusicTrack,
): Music {
  const context = out.context;
  let far: Reverb | null = null;
  const wired = new WeakSet<GainNode>();
  let current = voice(options.track);
  let leaving: { voice: Voice; state: SimState } | null = null;
  // Last heard before a path: the stage left goes on from it, apart from the state the sim changes.
  let stage: SimState | null = null;
  let leftAt: number | null = null;

  function voice(track: MusicTrack): Voice {
    const input = context.createBiquadFilter();
    input.type = 'lowpass';
    input.frequency.value = OPEN_HZ;
    const gain = context.createGain();
    const send = context.createGain();
    send.gain.value = 0;
    input.connect(gain).connect(out);
    gain.connect(send);
    const made: Voice = {
      input,
      gain,
      send,
      music: createMusic(input, {
        ...options,
        track,
        onKickScheduled: (tick, time) => {
          if (made === current) options.onKickScheduled?.(tick, time);
        },
      }),
    };
    return made;
  }

  function mix({ input, gain, send }: Voice, to: Mix, at: number) {
    // The night's echo is made on the first path, not in every game.
    if (to.wet > 0 && !wired.has(send)) {
      far ??= createReverb(out, FAR_ROOM);
      send.connect(far.input);
      wired.add(send);
    }
    gain.gain.setTargetAtTime(to.gain, at, GLIDE_SECONDS);
    input.frequency.setTargetAtTime(to.cutoff, at, GLIDE_SECONDS);
    send.gain.setTargetAtTime(to.wet, at, GLIDE_SECONDS);
  }

  function retire(gone: Voice, at: number) {
    gone.gain.gain.setTargetAtTime(0, at, GLIDE_SECONDS);
    gone.send.gain.setTargetAtTime(0, at, GLIDE_SECONDS);
    setTimeout(() => {
      gone.gain.disconnect();
      gone.send.disconnect();
    }, TAIL_SECONDS * 1000);
  }

  function depart(state: SimState, currentTime: number) {
    leftAt = state.tick;
    if (stage !== null) {
      leaving = { voice: current, state: stage };
    } else {
      retire(current, currentTime);
    }
    const set = options.setOf?.(state.setId);
    const playing = current.music.track();
    const track =
      set === undefined || set.trackIds.includes(playing.id)
        ? playing
        : (trackFor?.(set) ?? playing);
    current = voice(track);
    mix(current, aheadMix(0), currentTime);
  }

  function arrive(state: SimState, currentTime: number) {
    mix(current, OPEN, currentTime);
    const clock = current.music.clock();
    if (state.festival?.phase === 'scene' && leftAt !== null && state.tick > leftAt) {
      const hit = context.createGain();
      hit.gain.value = IMPACT_GAIN;
      hit.connect(current.input);
      impact(
        hit,
        Math.max(clock === null ? currentTime : tickToTime(clock, state.tick), currentTime),
      );
    }
    leftAt = null;
  }

  function leave(currentTime: number) {
    if (leaving !== null) {
      retire(leaving.voice, currentTime);
      leaving = null;
    }
  }

  return {
    update(state, now, currentTime) {
      const walked = pathWalked(state);
      if (walked !== null && leftAt === null) {
        depart(state, currentTime);
      } else if (walked === null && leftAt !== null) {
        leave(currentTime);
        arrive(state, currentTime);
      }
      if (leaving !== null) {
        if (walked === null || walked >= LEFT_SILENT) {
          leave(currentTime);
        } else {
          leaving.state.tick = state.tick;
          leaving.voice.music.update(leaving.state, now, currentTime);
          mix(leaving.voice, leftMix(walked), currentTime);
        }
      }
      current.music.update(state, now, currentTime);
      if (walked === null) {
        stage = { ...state, set: { ...state.set }, events: [] };
      } else {
        mix(current, aheadMix(walked), currentTime);
      }
    },
    menuAt(currentTime) {
      return current.music.menuAt(currentTime);
    },
    fadeOut(currentTime) {
      leave(currentTime);
      return current.music.fadeOut(currentTime);
    },
    fadeIn() {
      current.music.fadeIn();
    },
    setTrack(track) {
      current.music.setTrack(track);
    },
    track() {
      return current.music.track();
    },
    tempo() {
      return current.music.tempo();
    },
    clock() {
      return current.music.clock();
    },
  };
}
